<script lang="ts" setup>
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import xAxisLineChart from "@p-v/packages/charts/xAxisLineChart/index.vue"
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import PieDoughuntChart from "@p-v/packages/charts/PieDoughuntChart/index.vue"
import DataText from "@p-v/packages/DataText.vue"
import TrendPercent from "@p-v/packages/Functional/TrendPercent/index.vue"
import type { ProductItem } from "./index.d.ts"
import { ref, watch } from "vue"

type ProductRowProps = {
  storeData: ProductItem
  index: number
}

const areaLineChartRef = ref<InstanceType<typeof xAxisLineChart>>()

const pieDoughuntChartRef = ref<InstanceType<typeof PieDoughuntChart>>()

const amountDataTextRef = ref<InstanceType<typeof DataText>>()

const countDataTextRef = ref<InstanceType<typeof DataText>>()

const props = withDefaults(defineProps<ProductRowProps>(), {
  storeData: () => ({
    ticketName: "",
    amount: 0,
    count: 0,
    compareAmountRate: 0,
    compareCountRate: 0,
    amountTrend: [],
    channelList: [],
  }),
  index: 0,
})

const dataNumberConfig = {
  thousandFormat: true,
  label: {
    show: true,
    value: "销量",
    style: { color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" },
  },
  number: {
    color: "#2BB7F7FF",
    fontSize: "24px",
    fontWeight: "bold",
  },
  suffix: {
    show: false,
  },
}

const trendPercentConfig: any = {
  base: {
    fontSize: "14px",
    color: "#fff",
    iconPosition: "before",
    showSign: true,
    animation: true,
  },
  label: {
    show: true,
    value: "同比",
    fontSize: "14px",
    color: "#FFFFFFA6",
    followValueChange: false,
  },
  positive: {
    color: "#EEBD45FF",
    fontSize: "14px",
    fontWeight: "bold",
  },
  negative: {
    color: "#63E7B7FF",
    fontSize: "14px",
    fontWeight: "bold",
  },
}

const pieConfig: any = {
  color: ["#2998FF", "#63E7B7", "#EEBD45", "#DA656B"],
  series: [
    {
      radius: ["60%", "80%"],
      center: ["20%", "50%"],
    },
  ],
  legend: {
    left: "40%",
    textStyle: {
      align: "left",
      fontSize: 15,
      color: "#FFF",
      rich: {
        n: {
          fontSize: 15,
          width: 120,
        },
        p: {
          fontSize: 15,
          width: 70,
        },
      },
    },
  },
}

const areaLineChartData = ref<{
  data: any[]
  config: any
}>({
  data: [],
  config: {},
})

const pieDoughuntChartData = ref<{
  data: any[]
  config: any
}>({
  data: [],
  config: {},
})

const amountDataTextData = ref<any>(0)

const countDataTextData = ref<any>(0)

const renderAreaData = () => {
  if (!areaLineChartRef.value) return
  if (!areaLineChartData.value.data.length) return
  areaLineChartRef.value.setData(areaLineChartData.value)
}

const renderPieData = () => {
  if (!pieDoughuntChartRef.value) return
  if (!pieDoughuntChartData.value.data.length) return
  pieDoughuntChartRef.value.setData(pieDoughuntChartData.value)
}

const renderAmountDataText = () => {
  if (!amountDataTextRef.value) return
  amountDataTextRef.value.setData(amountDataTextData.value)
}

const renderCountDataText = () => {
  if (!countDataTextRef.value) return
  countDataTextRef.value.setData(countDataTextData.value)
}

watch(() => areaLineChartRef.value, renderAreaData)
watch(() => pieDoughuntChartRef.value, renderPieData)
watch(() => amountDataTextRef.value, renderAmountDataText)
watch(() => countDataTextRef.value, renderCountDataText)

watch(
  () => props.storeData,
  product => {
    console.log("watch product", props.index, product)
    if (product) {
      const { amountTrend = [], channelList = [], count, amount } = product

      amountDataTextData.value = amount
      countDataTextData.value = count
      renderAmountDataText()
      renderCountDataText()
      {
        const axis = amountTrend.map((item: any) => item.timeRange)
        const data = amountTrend.map((item: any) => item.ticketCount)
        areaLineChartData.value = {
          data: [
            {
              name: "票数",
              axis,
              showXAxis: true,
              data,
              unit: "票数",
              config: {
                serie: {
                  areaStyle: {
                    color: {
                      type: "linear",
                      x: 0,
                      y: 0,
                      x2: 0,
                      y2: 1,
                      colorStops: [
                        {
                          offset: 0,
                          color: "#2BB7F7", // 0% 处的颜色
                        },
                        {
                          offset: 1,
                          color: "rgba(0,0,0,0)", // 100% 处的颜色
                        },
                      ],
                      global: false, // 缺省为 false
                    },
                  },
                },
              },
            },
          ],
          config: {
            legend: {
              top: 0,
              right: 0,
              selectedMode: false,
            },
            grid: {
              top: 25,
              left: 15,
              right: 15,
              bottom: 25,
            },
            xAxis: {
              axisLine: {
                lineStyle: {
                  type: "dashed",
                },
              },
            },
            color: ["#2BB7F7", "#DADADA"],
          },
        }
        renderAreaData()
      }

      {
        pieDoughuntChartData.value = {
          data: channelList.map((item: any) => ({
            name: item.channelName,
            value: item.ticketCount,
          })),
          config: {
            legend: {
              formatter: (name: string) => {
                const item = channelList.find((item: any) => item.channelName === name)
                return `{n|${name}}{p|${item?.rate || 0}%}`
              },
            },
          },
        }
        renderPieData()
      }
    }
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <div class="table-row" flex="dir:left box:first cross:center">
    <div class="serial title-text">{{ index }}</div>
    <div class="name" flex="dir:top main:center">
      <div class="title-text ellipsis">
        {{ storeData.ticketName }}
      </div>
      <div flex="dir:left box:mean" style="margin-top: 10px">
        <div flex="dir:left cross:bottom">
          <DataText
            title="销量"
            flex="dir:top main:justify cross:top"
            :style="{
              marginRight: '14px',
            }"
            ref="countDataTextRef"
          ></DataText>
          <div style="width: 140px; flex-shrink: 0">
            <TrendPercent :options="trendPercentConfig" :data="{ value: storeData.compareCountRate }"></TrendPercent>
          </div>
        </div>
        <div flex="dir:left cross:bottom">
          <DataText
            title="销售额"
            flex="dir:top main:justify cross:top"
            :style="{
              marginRight: '14px',
            }"
            ref="amountDataTextRef"
          ></DataText>

          <div style="width: 140px; flex-shrink: 0">
            <TrendPercent :options="trendPercentConfig" :data="{ value: storeData.compareAmountRate }"></TrendPercent>
          </div>
        </div>
      </div>
    </div>
    <div class="trend" style="height: 100%">
      <div style="width: 100%; height: 100%; padding: 8px; box-sizing: border-box">
        <xAxisLineChart ref="areaLineChartRef" pointerTheme="one" style="height: 100%"></xAxisLineChart>
      </div>
    </div>
    <div class="analysis" flex="dir:left box:first">
      <div style="width: 100%; height: 130px; padding: 0 4px">
        <PieDoughuntChart ref="pieDoughuntChartRef" style="height: 100%" :config="pieConfig"></PieDoughuntChart>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.title-text {
  color: rgba(255, 255, 255);
  font-size: 24px;
  font-weight: bold;
}

.table-row {
  height: 142px;
  background: rgba(41, 61, 96, 0.5);
  margin-bottom: 16px;

  .serial {
    min-width: 118px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.fake-legend-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, 1fr);
  grid-gap: 10px;
  padding: 25px 10px;

  .name {
    margin: 0 12px 0 4px;
  }
}
</style>
